import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  type SavedProperty,
  deleteSavedProperty,
  getUserSaved,
  setSavedMute,
} from "../api/client";
import { ExteriorThumbnail } from "../components/ExteriorViewCard";
import { NavBar } from "../components/NavBar";
import { LoadingState } from "../components/LoadingState";
import { TiltMapChart } from "../components/TiltMapChart";

/**
 * SavedPage — Task 9.4. Paid-tier dashboard of saved properties.
 * Each row: address, saved date, baseline AVM, actions (compare-select,
 * mute alerts, remove). Compare selection routes to /compare with the
 * chosen place_ids.
 */
// Distinct, evenly-spaced hues so each saved property's dot is identifiable on
// the map (esp. on mobile, where hover doesn't work) — matched in the legend.
function dotColor(i: number, n: number): string {
  return `hsl(${Math.round((i * 360) / Math.max(n, 1))}, 70%, 45%)`;
}

export function SavedPage() {
  const navigate = useNavigate();
  const [saved, setSaved] = useState<SavedProperty[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [muted, setMuted] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    getUserSaved()
      .then((r) => {
        if (!cancelled) setSaved(r.saved);
      })
      .catch((e: { message?: string }) => {
        if (!cancelled) setError(e.message || "Could not load saved properties.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function toggleSelect(placeId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(placeId)) next.delete(placeId);
      else if (next.size < 5) next.add(placeId);
      return next;
    });
  }

  async function handleMute(placeId: string) {
    const willMute = !muted.has(placeId);
    try {
      await setSavedMute(placeId, willMute);
      setMuted((prev) => {
        const next = new Set(prev);
        if (willMute) next.add(placeId);
        else next.delete(placeId);
        return next;
      });
    } catch {
      /* best-effort; leave state unchanged on failure */
    }
  }

  async function handleRemove(placeId: string) {
    try {
      await deleteSavedProperty(placeId);
      setSaved((prev) => (prev ? prev.filter((s) => s.place_id !== placeId) : prev));
    } catch {
      /* ignore */
    }
  }

  // Saved-A: re-score the property to bring up its report. We don't store a
  // score_id on the saved record, so we stash the property and route home,
  // where App prefills the form (add price → fresh Pro report).
  function viewReport(s: SavedProperty) {
    if (!s.address) return;
    try {
      sessionStorage.setItem(
        "pt.prefillProperty",
        JSON.stringify({
          address: s.address,
          placeId: s.place_id,
          // Saved with a price → App re-scores directly (no re-prompt).
          askingPrice: s.asking_price,
        }),
      );
    } catch {
      /* ignore */
    }
    navigate("/");
  }

  return (
    <div className="app">
      <NavBar />
      <main>
        <header className="page-header">
          <h1>Saved properties</h1>
          {selected.size >= 2 && (
            <button
              type="button"
              className="submit"
              onClick={() =>
                navigate(`/compare?place_ids=${encodeURIComponent([...selected].join(","))}`)
              }
            >
              Compare {selected.size} selected
            </button>
          )}
        </header>

        {error && <p className="error" role="alert">{error}</p>}
        {!saved && !error && <LoadingState message="Loading saved properties…" />}

        {saved && saved.length === 0 && (
          <p className="empty-state">
            No saved properties yet. Score a property and save it to track
            changes over time.
          </p>
        )}

        {saved && saved.length > 0 && (
          <TiltMapChart
            title="Your Tilt Map"
            points={saved.map((s, i) => ({
              id: s.place_id,
              label: s.address ?? s.place_id,
              tiltMap: s.tilt_map,
              color: dotColor(i, saved.length),
              onClick: () => viewReport(s),
            }))}
          />
        )}

        {saved && saved.length > 0 && (
          <ul className="saved-list">
            {saved.map((s) => (
              <li key={s.place_id} className="saved-row">
                <label className="saved-select">
                  <input
                    type="checkbox"
                    checked={selected.has(s.place_id)}
                    onChange={() => toggleSelect(s.place_id)}
                    aria-label={`Select ${s.address ?? s.place_id} for comparison`}
                  />
                </label>
                <ExteriorThumbnail placeId={s.place_id} />
                <div className="saved-main">
                  {s.address ? (
                    <button
                      type="button"
                      className="saved-address-link"
                      onClick={() => viewReport(s)}
                      title="View report (re-scores with fresh data)"
                    >
                      {s.address}
                    </button>
                  ) : (
                    <div className="saved-address">{s.place_id}</div>
                  )}
                  <div className="saved-meta">
                    Saved {new Date(s.saved_at).toLocaleDateString()}
                    {s.asking_price != null &&
                      ` · ask $${s.asking_price.toLocaleString()}`}
                    {s.baseline_avm != null &&
                      ` · baseline AVM $${s.baseline_avm.toLocaleString()}`}
                  </div>
                </div>
                <div className="saved-actions">
                  <button type="button" onClick={() => void handleMute(s.place_id)}>
                    {muted.has(s.place_id) ? "Unmute" : "Mute"}
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => void handleRemove(s.place_id)}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {saved && saved.length > 0 && selected.size < 2 && (
          <p className="hint">Select 2-5 properties to compare them side-by-side.</p>
        )}
      </main>
    </div>
  );
}
